<template>
    <el-dialog title="品名配置" :visible.sync="visibleSync" width="1250px" custom-class="pm-config-dialog"
        :close-on-click-modal="false" append-to-body @open="onOpen">
        <div class="config-meta">
            <span class="meta-item"><span class="meta-label">本地父体</span>{{ parent }}</span>
            <span class="meta-item"><span class="meta-label">本地品名</span>{{ pm }}</span>
            <span class="meta-item"><span class="meta-label">店铺</span>{{ shop }}</span>
            <span class="meta-item"><span class="meta-label">MSKU</span>{{ msku || '-' }}</span>
            <span v-if="suggestedColor" class="meta-item"><span class="meta-label">识别颜色</span>{{ suggestedColor }}</span>
        </div>

        <!-- 从现有品名配置表中搜索模板，仅套用到当前表单；修改后仍需点击保存。 -->
        <div class="pm-template-apply">
            <div class="pm-template-title"><i class="el-icon-document-copy"></i> 套用已配置品名模板</div>
            <div class="pm-template-controls">
                <el-input v-model="pmTemplateSearch" size="small" clearable
                    placeholder="输入品名，支持模糊查询" prefix-icon="el-icon-search"
                    style="width:300px" @input="searchPmTemplates" />
                <el-select v-model="pmTemplateSelectedKey" size="small" filterable clearable
                    :loading="pmTemplateSearchLoading" :disabled="!pmTemplateOptions.length"
                    placeholder="选择要套用的已配置品名" style="width:520px">
                    <el-option v-for="item in pmTemplateOptions" :key="pmTemplateKey(item)"
                        :label="pmTemplateLabel(item)" :value="pmTemplateKey(item)">
                        <span>{{ item['本地品名'] }}</span>
                        <span style="float:right;color:#909399;font-size:12px;">
                            {{ item['本地父体'] }} / {{ item['店铺'] }}
                        </span>
                    </el-option>
                </el-select>
                <el-button type="success" plain size="small" :disabled="!pmTemplateSelectedKey"
                    @click="applyPmTemplate">应用到当前品名</el-button>
            </div>
            <div class="msku-hint">数据来源：品名配置表（支持跨父体、跨店铺搜索）。应用只改变当前表单，确认修改后点击底部“保存配置”才会写入当前品名。</div>
        </div>

        <!-- 品名自身配置（父体配置默认继承，可在本弹窗内临时覆盖用于生成） -->
        <div class="config-layout">
            <div class="config-left">
                <el-form label-width="90px" size="small">
                    <el-form-item label="产品类型">
                        <el-select v-model="configForm.product_type" filterable clearable placeholder="选择产品类型"
                            style="width:100%">
                            <el-option v-for="pt in productTypes" :key="pt.value" :label="pt.label" :value="pt.value" />
                        </el-select>
                        <div class="msku-hint">默认继承父体配置，品名可单独设置覆盖</div>
                    </el-form-item>
                    <el-divider content-position="left">品名自身配置（留空继承父体）</el-divider>
                    <el-form-item label="系列">
                        <el-input v-model="configForm.series" placeholder="如: K22" />
                        <div class="msku-hint">留空则继承父体配置</div>
                    </el-form-item>
                    <el-form-item label="款式">
                        <el-input v-model="configForm.style" placeholder="如: 壳子款 / 边贴款" />
                        <div class="msku-hint">留空则继承父体配置，同父体不同款式品名可各自设置</div>
                    </el-form-item>
                    <el-form-item label="MSKU颜色">
                        <el-input v-model="configForm.create_field_values.msku_color" placeholder="如: Black" />
                        <div class="msku-hint">优先用于 MSKU 属性拼接；留空时读取模板表标记的 color，仍为空再取品名识别颜色</div>
                    </el-form-item>
                    <el-form-item label="生成模式">
                        <el-radio-group v-model="configForm.use_msku_template" size="small">
                            <el-radio-button :label="1">按模板</el-radio-button>
                            <el-radio-button :label="0">按属性</el-radio-button>
                        </el-radio-group>
                        <div class="msku-hint">默认继承父体配置，品名可单独设置</div>
                    </el-form-item>
                    <el-form-item label="MSKU 分隔符">
                        <el-input v-model="configForm.msku_separator" style="width:100px" />
                        <div class="msku-hint">默认继承父体配置</div>
                    </el-form-item>
                    <el-form-item label="MSKU 模板">
                        <el-input v-model="configForm.msku_template" placeholder="如: CB-KPW5"
                            :maxlength="mskuMaxLen(configForm.supplier)" show-word-limit />
                        <div class="msku-hint">留空继承父体配置｜模板最长 {{ mskuMaxLen(configForm.supplier) }} 字符</div>
                    </el-form-item>
                    <el-form-item label="msku配置-供应商">
                        <el-input v-model="configForm.supplier" placeholder="如: JT" />
                        <div class="msku-hint">留空继承父体配置（仅用于生成MSKU）</div>
                    </el-form-item>
                    <el-form-item label="msku配置-国家">
                        <el-input v-model="configForm.country" placeholder="如: 美国" />
                        <div class="msku-hint">留空继承父体配置（仅用于生成MSKU）</div>
                    </el-form-item>
                    <el-form-item v-if="showWarehouse" label="仓库" required>
                        <el-select v-model="warehouseValue" clearable filterable placeholder="请选择采购仓库" style="width:100%">
                            <el-option v-for="item in warehouses" :key="item.value" :label="item.text" :value="item.value" />
                        </el-select>
                    </el-form-item>
                    <el-form-item label="Parent SKU">
                        <el-input v-model="configForm.parent_sku" placeholder="父体 MSKU" />
                    </el-form-item>
                    <el-form-item label="Variation">
                        <el-select v-model="configForm.variation_theme" placeholder="选择变体主题" clearable
                            filterable style="width:100%">
                            <el-option v-for="vt in configVariationThemes" :key="vt.value" :label="vt.label"
                                :value="vt.value" />
                        </el-select>
                    </el-form-item>
                    <el-form-item label="创建主标题">
                        <el-input v-model="configForm.create_main_title" placeholder="如: CoBak 6&quot; Case" />
                    </el-form-item>
                    <el-form-item label="创建副标题">
                        <el-input v-model="configForm.create_sub_title" placeholder="副标题基础" />
                    </el-form-item>
                    <el-form-item label="移出标题">
                        <el-input v-model="configForm.move_out_title" placeholder="移出主标题模板" />
                    </el-form-item>
                    <el-form-item label="移出副标题">
                        <el-input v-model="configForm.move_out_title_differentiation" placeholder="移出副标题模板" />
                    </el-form-item>
                </el-form>
            </div>
            <div class="config-right">
                <el-tabs v-model="activeScene">
                    <el-tab-pane label="创建字段" name="create">
                        <el-form label-width="120px" size="small">
                            <el-form-item v-for="f in pmFields.create" :key="'c-' + f.field_name"
                                :label="f.title_zh || f.field_name">
                                <div v-if="f.field_name === 'item_package_dimensions'" class="dims-row">
                                    <el-input-number v-model="configForm.create_field_values[f.field_name].length"
                                        :min="0" :precision="2" controls-position="right" size="small" style="width:110px" />
                                    <span class="dims-x">×</span>
                                    <el-input-number v-model="configForm.create_field_values[f.field_name].width"
                                        :min="0" :precision="2" controls-position="right" size="small" style="width:110px" />
                                    <span class="dims-x">×</span>
                                    <el-input-number v-model="configForm.create_field_values[f.field_name].height"
                                        :min="0" :precision="2" controls-position="right" size="small" style="width:110px" />
                                    <el-select v-model="configForm.create_field_values[f.field_name].unit" size="small"
                                        style="width:140px" placeholder="单位">
                                        <el-option v-for="u in dimsUnitOptions(f)" :key="u.value" :label="u.label"
                                            :value="u.value" />
                                    </el-select>
                                </div>
                                <div v-else-if="f.field_name === 'item_package_weight'" class="dims-row">
                                    <el-input-number v-model="configForm.create_field_values[f.field_name].value"
                                        :min="0" :precision="2" controls-position="right" size="small" style="width:160px" />
                                    <el-select v-model="configForm.create_field_values[f.field_name].unit" size="small"
                                        style="width:140px" placeholder="单位">
                                        <el-option v-for="u in weightUnitOptions(f)" :key="u.value" :label="u.label"
                                            :value="u.value" />
                                    </el-select>
                                </div>
                                <el-switch v-else-if="isBoolField(f)"
                                    v-model="configForm.create_field_values[f.field_name]"
                                    active-text="Yes" inactive-text="No" />
                                <el-select v-else-if="hasEnum(f)" v-model="configForm.create_field_values[f.field_name]"
                                    filterable allow-create default-first-option clearable style="width:100%"
                                    :placeholder="f.field_name">
                                    <el-option v-for="opt in f.enum" :key="opt" :label="opt" :value="opt" />
                                </el-select>
                                <el-input-number v-else-if="isNumberField(f)"
                                    v-model="configForm.create_field_values[f.field_name]"
                                    controls-position="right" style="width:100%" />
                                <el-input v-else v-model="configForm.create_field_values[f.field_name]"
                                    :placeholder="f.field_name" />
                            </el-form-item>
                            <div v-if="!pmFields.create.length" class="config-empty">该产品类型下暂无品名级创建字段</div>
                        </el-form>
                    </el-tab-pane>
                    <el-tab-pane label="MSKU 生成" name="msku_gen">
                        <el-form label-width="110px" size="small">
                            <el-form-item label="生成模式">
                                <el-tag size="small" type="info" effect="plain">
                                    {{ genMode === 'attribute' ? '按属性拼接' : '按模板生成' }}</el-tag>
                                <div class="msku-hint">跟随品名配置的生成模式（品名级优先，留空继承父体），如需切换请修改左侧「生成模式」配置项</div>
                            </el-form-item>
                            <el-form-item label="基础名预览">
                                <div v-if="genMode === 'template'" class="msku-preview"
                                    :class="{ 'msku-preview-warn': baseOverlong }">{{ genTemplatePreview }}</div>
                                <div v-else class="msku-preview"
                                    :class="{ 'msku-preview-warn': baseOverlong }">{{ genAttributePreview }}</div>
                                <div v-if="baseOverlong" class="msku-preview-warn-text">
                                    当前配置基础名 {{ basePreviewLen }} 字符，超出最大 {{ maxBaseLen }} 字符，
                                    生成的 MSKU 将被截断（{{ genBasePreviewText }} →
                                    {{ genBasePreviewText.slice(0, maxBaseLen) }}），请检查配置字符是否超出
                                </div>
                            </el-form-item>
                            <el-form-item label="数量">
                                <el-input-number v-model="genForm.num" :min="1" :max="200" />
                            </el-form-item>
                            <el-form-item label="后缀">
                                <el-radio-group v-model="genForm.suffix" size="small">
                                    <el-radio-button label="T">T（创建发货产品）</el-radio-button>
                                    <el-radio-button label="C">C（创建跟卖）</el-radio-button>
                                    <el-radio-button label="CUSTOM">自定义</el-radio-button>
                                </el-radio-group>
                                <el-input v-if="genForm.suffix === 'CUSTOM'" v-model="genForm.customSuffix"
                                    maxlength="6" placeholder="如 HB" style="width:120px;margin-left:8px;" />
                            </el-form-item>
                            <el-form-item>
                                <el-button type="primary" @click="generateMsku" :loading="genLoading">生成 MSKU</el-button>
                                <el-button v-if="genResult && genResult.mskus && genResult.mskus.length"
                                    @click="copyMskus">复制全部</el-button>
                                <el-button v-if="genResult && genResult.mskus && genResult.mskus.length"
                                    @click="downloadMsku">下载 Excel</el-button>
                            </el-form-item>
                        </el-form>
                        <el-alert v-if="genError" :title="genError" type="error" :closable="false" show-icon
                            style="margin-bottom:10px;" />
                        <el-alert v-if="genResult && genResult.truncated" type="warning" :closable="false" show-icon
                            style="margin-bottom:10px;">
                            <template slot="title">
                                当前生成模式基础名超长已被截断：基础名 {{ genResult.base_len }} 字符超出最大
                                {{ genResult.max_base_len }} 字符（{{ genResult.base }} →
                                {{ (genResult.base || '').slice(0, genResult.max_base_len) }}），
                                请检查配置字符是否超出
                            </template>
                        </el-alert>
                        <el-alert v-if="genResult && genResult.base"
                            :title="'基础名: ' + genResult.base + '（共 ' + genResult.mskus.length + ' 个）'"
                            type="info" :closable="false" style="margin-bottom:10px;" />
                        <el-alert v-if="genResult && genResult.color && genResult.color.matched"
                            :title="'使用颜色: ' + (genResult.color['配置颜色'] || genResult.color['品名颜色'] || '-') + ' ｜ 站点颜色翻译: ' + (genResult.color['站点翻译'] || '-')"
                            type="success" :closable="false" show-icon style="margin-bottom:10px;" />
                        <el-alert v-if="genResult && genResult.color && !genResult.color.matched && genResult.color['配置颜色']"
                            :title="'自定义颜色「' + genResult.color['配置颜色'] + '」不在颜色词典中（需完全匹配），站点翻译置空'"
                            type="warning" :closable="false" show-icon style="margin-bottom:10px;" />
                        <div v-if="genResult && genResult.mskus && genResult.mskus.length"
                            style="max-height:300px;overflow-y:auto;border:1px solid #ebeef5;border-radius:4px;padding:8px;">
                            <el-tag v-for="m in genResult.mskus" :key="m" size="small" style="margin:2px 4px 2px 0;">
                                {{ m }}</el-tag>
                        </div>
                    </el-tab-pane>
                </el-tabs>
            </div>
        </div>

        <span slot="footer">
            <el-button @click="visibleSync = false">取消</el-button>
            <el-button @click="applyParentConfig">应用父体配置</el-button>
            <el-button type="primary" :loading="configSaving" @click="saveConfig">保存配置</el-button>
        </span>
    </el-dialog>
</template>

<script>
export default {
    name: 'PmConfigDialog',
    props: {
        visible: { type: Boolean, default: false },
        parent: { type: String, default: '' },
        pm: { type: String, default: '' },
        shop: { type: String, default: '' },
        msku: { type: String, default: '' },
        warehouse: { type: String, default: '' },
        warehouses: { type: Array, default: () => [] },
        showWarehouse: { type: Boolean, default: true },
    },
    data() {
        return {
            configSaving: false,
            activeScene: 'create',
            productTypes: [],
            configVariationThemes: [],
            configForm: this.blankConfigForm(),
            pmFields: { create: [], move_in: [], move_out: [] },
            // MSKU 生成
            genMode: 'template',
            genLoading: false,
            genResult: null,
            genError: '',
            genForm: { num: 10, suffix: 'T', customSuffix: '' },
            suggestedColor: '',
            // 父体公用字段值（父体级字段的继承默认值）
            parentFieldValues: {},
            // 已配置品名模板搜索/套用
            pmTemplateSearch: '',
            pmTemplateOptions: [],
            pmTemplateSelectedKey: '',
            pmTemplateSearchLoading: false,
            pmTemplateSearchTimer: null,
            pmTemplateSearchToken: 0,
        };
    },
    beforeDestroy() {
        if (this.pmTemplateSearchTimer) clearTimeout(this.pmTemplateSearchTimer);
    },
    watch: {
        // 品名弹窗内手动选择/品名配置带出产品类型后，加载对应字段（父体未配置时也能用）
        'configForm.product_type'(pt, oldPt) {
            if (pt && pt !== oldPt) this.loadAllFieldsAndThemes();
        },
        // 品名自身生成模式变更 → 同步 MSKU 生成 Tab 的模式
        'configForm.use_msku_template'(v) {
            this.genMode = (v === 0 || v === false || v === '0') ? 'attribute' : 'template';
        },
    },
    computed: {
        visibleSync: {
            get() { return this.visible; },
            set(v) { this.$emit('update:visible', v); },
        },
        warehouseValue: {
            get() { return this.warehouse; },
            set(v) { this.$emit('update:warehouse', v); },
        },
        genSuffixLabel() {
            return this.effectiveGenSuffix() || 'T';
        },
        genTemplatePreview() {
            return this.configForm.msku_template
                ? this.configForm.msku_template + '-{供应商}-{国家缩写}-{MMDD}{随机}{序号}-{' + this.genSuffixLabel + '}'
                : '未配置 MSKU 模板';
        },
        genAttributePreview() {
            const sep = this.configForm.msku_separator || '-';
            const parts = [];
            if (this.configForm.series) parts.push(this.configForm.series);
            if (this.configForm.style) parts.push(this.configForm.style);
            // MSKU 颜色生成属性：msku_color 优先，其次读取模板表标记的 color
            const color = this.findConfigFieldValue('msku_color')
                || this.findMskuPartFieldValue('color') || this.suggestedColor;
            const shell = this.findMskuPartFieldValue('shell_type');
            if (color) parts.push(color);
            else if (shell) parts.push(shell);
            return parts.length
                ? parts.join(sep) + '-{供应商}-{国家缩写}-{MMDD}{随机}{序号}-{' + this.genSuffixLabel + '}'
                : '未配置系列/款式/颜色，无法预览';
        },
        // 基础名预览超长检测（与后端 generate_msku_batch 口径一致：40 字符上限）
        genBasePreviewText() {
            if (this.genMode === 'template') {
                return (this.configForm.msku_template || '').trim();
            }
            const sep = this.configForm.msku_separator || '-';
            const parts = [];
            if (this.configForm.series) parts.push(this.configForm.series);
            if (this.configForm.style) parts.push(this.configForm.style);
            // MSKU 颜色生成属性：msku_color 优先，其次读取模板表标记的 color
            const color = this.findConfigFieldValue('msku_color')
                || this.findMskuPartFieldValue('color') || this.suggestedColor;
            const shell = this.findMskuPartFieldValue('shell_type');
            if (color) parts.push(color);
            else if (shell) parts.push(shell);
            return parts.join(sep);
        },
        maxBaseLen() {
            // 固定后缀段：-{供应商}-{国家缩写}-{MMDD}{3位随机}{序号}-{T/C}
            const COUNTRY_ABBR = {
                '美国': 'US', '加拿大': 'CA', '墨西哥': 'MX', '英国': 'UK', '意大利': 'IT',
                '德国': 'DE', '法国': 'FR', '西班牙': 'ES', '印度': 'IN', '日本': 'JP',
                '澳洲': 'AU', '阿联酋': 'AE', '新加坡': 'SG', '荷兰': 'NL', '沙特阿拉伯': 'SA',
                '巴西': 'BR', '瑞典': 'SE', '土耳其': 'TR', '波兰': 'PL',
            };
            const supplier = this.configForm.supplier || '';
            const country = this.configForm.country || '';
            const abbr = COUNTRY_ABBR[country] || (country || '').toUpperCase().slice(0, 2);
            const suffix = (this.effectiveGenSuffix() || 'T').toUpperCase();
            const coreLen = 1 + supplier.length + 1 + abbr.length + 1 + 4 + 3 + 3 + 1 + suffix.length;
            return Math.max(1, 40 - coreLen);
        },
        basePreviewLen() {
            return this.genBasePreviewText.length;
        },
        baseOverlong() {
            return this.basePreviewLen > 0 && this.basePreviewLen > this.maxBaseLen;
        },
    },
    methods: {
        blankConfigForm() {
            return {
                '本地父体': this.parent || '',
                '本地品名': this.pm || '',
                '店铺': this.shop || '',
                product_type: '', msku_template: '', supplier: '', country: '',
                parent_sku: '', variation_theme: '',
                series: '', style: '', use_msku_template: 1, msku_separator: '-',
                create_main_title: '', create_sub_title: '',
                move_out_title: '', move_out_title_differentiation: '',
                create_field_values: {}, move_in_field_values: {}, move_out_field_values: {},
            };
        },
        onOpen() {
            this.configForm = this.blankConfigForm();
            // MSKU 颜色生成属性：预先建立响应式键（Vue2 无法侦测后续新增的属性）
            this.$set(this.configForm.create_field_values, 'msku_color', '');
            // MSKU 生成页签的模式固定跟随品名配置（防止上一个品名的模式残留）；
            // 品名配置加载后 use_msku_template 变化会经 watch 再次同步
            this.genMode = 'template';
            this.pmFields = { create: [], move_in: [], move_out: [] };
            this.configVariationThemes = [];
            this.activeScene = 'create';
            this.genResult = null;
            this.genError = '';
            this.genForm = { num: 10, suffix: 'T', customSuffix: '' };
            this.pmTemplateSearch = '';
            this.pmTemplateOptions = [];
            this.pmTemplateSelectedKey = '';
            this.pmTemplateSearchLoading = false;
            this.pmTemplateSearchToken += 1;
            if (this.pmTemplateSearchTimer) {
                clearTimeout(this.pmTemplateSearchTimer);
                this.pmTemplateSearchTimer = null;
            }
            if (!this.productTypes.length) this.fetchProductTypes();
            this.suggestedColor = '';
            this.fetchSuggestedColor();
            if (!this.parent || !this.pm || !this.shop) return;
            // 先加载父体配置（继承产品类型/系列/款式/标题），再加载品名自身配置覆盖
            this.loadParentConfig(() => this.loadPmConfig());
        },
        // 加载父体配置：产品类型/系列/款式/生成模式/标题默认继承父体（品名自身配置会在此之后覆盖）
        loadParentConfig(cb) {
            this.$axios.get('/vue/vue_product/parent-config', {
                params: { parent: this.parent }
            }).then(res => {
                if (res.code === 200 && res.data) {
                    const d = res.data;
                    this.configForm.product_type = d.product_type || this.configForm.product_type;
                    this.configForm.create_main_title = d.create_main_title || '';
                    this.configForm.create_sub_title = d.create_sub_title || '';
                    this.configForm.move_out_title = d.move_out_title || '';
                    this.configForm.move_out_title_differentiation = d.move_out_title_differentiation || '';
                    // 品名级可覆盖字段：留空继承父体
                    this.configForm.series = d.series || '';
                    this.configForm.style = d.style || '';
                    this.configForm.use_msku_template =
                        (d.use_msku_template === 0 || d.use_msku_template === false || d.use_msku_template === '0') ? 0 : 1;
                    this.configForm.msku_separator = d.msku_separator || '-';
                    // 父体公用字段值（父体级创建字段的继承默认值）
                    this.parentFieldValues = d.parent_field_values || {};
                    this.loadAllFieldsAndThemes();
                }
                if (typeof cb === 'function') cb();
            }).catch(() => {
                if (typeof cb === 'function') cb();
            });
        },
        loadPmConfig() {
            this.$axios.get('/vue/vue_product/operation-config', {
                params: { parent: this.parent, pm: this.pm, shop: this.shop }
            }).then(res => {
                if (res.code === 200 && res.data) {
                    const d = res.data;
                    const prevPt = this.configForm.product_type;
                    Object.keys(this.configForm).forEach(k => {
                        if (d[k] === undefined || d[k] === null || d[k] === '') return;
                        // 场景字段值必须合并，不能整表覆盖：loadAllFieldsAndThemes 已按字段
                        // 生成了 item_package_dimensions / item_package_weight 等默认对象，
                        // 整表覆盖会导致这些字段缺失，模板渲染访问 undefined 报错、弹窗卡死。
                        if (k.endsWith('_field_values')) {
                            const cur = this.configForm[k] || {};
                            const src = d[k] || {};
                            Object.keys(src).forEach(fn => {
                                this.$set(cur, fn, src[fn]);
                            });
                        } else {
                            this.configForm[k] = d[k];
                        }
                    });
                    this.normalizeWeightFields();
                    // 若品名配置带出不同产品类型，需按最终类型重新加载字段（同时补齐缺失默认值）
                    if (this.configForm.product_type && this.configForm.product_type !== prevPt) {
                        this.loadAllFieldsAndThemes();
                    }
                    this.applySuggestedColor();
                }
            }).catch(() => { });
        },
        loadAllFieldsAndThemes() {
            const pt = this.configForm.product_type;
            if (!pt) return;
            // 加载全部创建字段（父体级 + 品名级），父体级默认继承父体公用字段值，品名可修改覆盖
            this.$axios.get('/vue/vue_product/operation-fields', {
                params: { product_type: pt, scene: 'create' }
            }).then(res => {
                if (res.code !== 200) return;
                const fields = res.data || [];
                const key = 'create_field_values';
                fields.forEach(f => {
                    if (this.configForm[key][f.field_name] === undefined) {
                        // 父体级字段：未配置品名时直接推荐父体配置的值
                        const level = f.config_level || 'pm';
                        let dv;
                        if (level === 'parent' && this.parentFieldValues &&
                            this.parentFieldValues[f.field_name] !== undefined &&
                            this.parentFieldValues[f.field_name] !== null &&
                            this.parentFieldValues[f.field_name] !== '') {
                            dv = this.parentFieldValues[f.field_name];
                        } else {
                            dv = this.defaultFieldValue(f);
                        }
                        this.$set(this.configForm[key], f.field_name, dv);
                    }
                    if (f.field_name === 'item_package_dimensions') {
                        const fv = this.configForm[key][f.field_name];
                        if (fv && typeof fv === 'object' && fv.unit === undefined) {
                            this.$set(fv, 'unit', 'inches');
                        }
                    }
                });
                this.normalizeWeightFields();
                this.$set(this.pmFields, 'create', fields);
                this.applySuggestedColor();
            }).catch(() => { });
            this.fetchConfigVariationThemes(pt);
        },
        fetchProductTypes() {
            this.$axios.get('/vue/vue_product/product-types').then(res => {
                if (res.code === 200 && res.data) this.productTypes = res.data;
            }).catch(() => { });
        },
        fetchConfigVariationThemes(productType) {
            if (!productType) { this.configVariationThemes = []; return; }
            this.$axios.get('/vue/vue_product/variation-themes', {
                params: { product_type: productType }
            }).then(res => {
                if (res.code === 200 && res.data) this.configVariationThemes = res.data;
            }).catch(() => { });
        },
        pmTemplateKey(item) {
            return [item['本地父体'] || '', item['本地品名'] || '', item['店铺'] || ''].join('|');
        },
        pmTemplateLabel(item) {
            return (item['本地品名'] || '') + '｜' + (item['本地父体'] || '') + '｜' + (item['店铺'] || '');
        },
        cloneConfigValue(value) {
            if (value === undefined || value === null) return value;
            if (typeof value !== 'object') return value;
            try {
                return JSON.parse(JSON.stringify(value));
            } catch (e) {
                return value;
            }
        },
        // 输入品名后从 product_operation_config 做模糊查询，避免一次性加载整张配置表。
        searchPmTemplates() {
            if (this.pmTemplateSearchTimer) clearTimeout(this.pmTemplateSearchTimer);
            const keyword = (this.pmTemplateSearch || '').trim();
            const token = ++this.pmTemplateSearchToken;
            this.pmTemplateOptions = [];
            this.pmTemplateSelectedKey = '';
            if (!keyword) {
                this.pmTemplateSearchLoading = false;
                return;
            }
            this.pmTemplateSearchTimer = setTimeout(() => {
                this.pmTemplateSearchLoading = true;
                this.$axios.get('/vue/vue_product/operation-config/search', {
                    params: { pm: keyword, limit: 30 }
                }).then(res => {
                    if (token !== this.pmTemplateSearchToken) return;
                    const rows = (res.code === 200 && Array.isArray(res.data)) ? res.data : [];
                    const currentKey = this.pmTemplateKey({
                        '本地父体': this.parent, '本地品名': this.pm, '店铺': this.shop,
                    });
                    this.pmTemplateOptions = rows.filter(item => this.pmTemplateKey(item) !== currentKey);
                }).catch(() => {
                    if (token === this.pmTemplateSearchToken) this.pmTemplateOptions = [];
                }).then(() => {
                    if (token === this.pmTemplateSearchToken) {
                        this.pmTemplateSearchLoading = false;
                        this.pmTemplateSearchTimer = null;
                    }
                });
            }, 250);
        },
        // 将选中的品名配置复制到当前品名表单，身份列始终保留当前父体/品名/店铺。
        applyPmTemplate() {
            const source = this.pmTemplateOptions.find(item =>
                this.pmTemplateKey(item) === this.pmTemplateSelectedKey);
            if (!source) return this.$message.warning('请先搜索并选择一个已配置品名模板');

            const scalarKeys = [
                'product_type', 'msku_template', 'supplier', 'country', 'parent_sku',
                'variation_theme', 'series', 'style', 'use_msku_template', 'msku_separator',
                'create_main_title', 'create_sub_title', 'move_out_title',
                'move_out_title_differentiation',
            ];
            scalarKeys.forEach(key => {
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    this.$set(this.configForm, key, this.cloneConfigValue(source[key]));
                }
            });
            ['create_field_values', 'move_in_field_values', 'move_out_field_values'].forEach(key => {
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    this.$set(this.configForm, key, this.cloneConfigValue(source[key]) || {});
                }
            });
            this.$set(this.configForm, '本地父体', this.parent || '');
            this.$set(this.configForm, '本地品名', this.pm || '');
            this.$set(this.configForm, '店铺', this.shop || '');

            this.normalizeWeightFields();
            this.genResult = null;
            this.genError = '';
            if (this.configForm.product_type) this.loadAllFieldsAndThemes();
            this.applySuggestedColor();
            this.$message.success('已套用品名模板，可继续修改后保存');
        },
        // === MSKU 生成（生成模式/系列/款式随品名配置，未保存时以表单当前值为临时覆盖）===
        // 从品名配置的三个场景字段值中查找某字段的非空值（如 color/shell_type）
        findConfigFieldValue(fieldName) {
            const keys = ['create_field_values', 'move_in_field_values', 'move_out_field_values'];
            for (const key of keys) {
                const fv = this.configForm[key] || {};
                const v = fv[fieldName];
                if (v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && v.length === 0)) {
                    return typeof v === 'object' ? JSON.stringify(v) : String(v);
                }
            }
            return '';
        },
        isMskuPartField(fieldName) {
            const fields = this.pmFields.create || [];
            if (fields.length) {
                const field = fields.find(f => f.field_name === fieldName);
                return !!field && (field.msku_part === true || field.msku_part === 1
                    || field.msku_part === '1' || field.msku_part === 'true');
            }
            // 字段元数据尚未加载或旧模板没有标记时的兼容兜底。
            return fieldName === 'color' || fieldName === 'shell_type';
        },
        findMskuPartFieldValue(fieldName) {
            return this.isMskuPartField(fieldName) ? this.findConfigFieldValue(fieldName) : '';
        },
        // 从品名识别颜色（词典匹配），命中则把英文翻译填入 msku_color 字段
        // （MSKU 颜色生成属性，可改；刊登上传的 color 字段不受影响，由创建字段页签单独维护）
        fetchSuggestedColor() {
            if (!this.pm) return;
            this.$axios.get('/vue/vue_product/color/extract', {
                params: { pm: this.pm }
            }).then(res => {
                if (res.code === 200 && res.data && res.data.matched) {
                    this.suggestedColor = res.data['英文翻译'] || '';
                } else {
                    this.suggestedColor = '';
                }
                this.applySuggestedColor();
            }).catch(() => { });
        },
        applySuggestedColor() {
            if (!this.suggestedColor) return;
            const fv = this.configForm.create_field_values || {};
            if (fv.msku_color === undefined || fv.msku_color === null || fv.msku_color === '') {
                this.$set(this.configForm.create_field_values, 'msku_color', this.suggestedColor);
            }
        },
        effectiveGenSuffix() {
            return this.genForm.suffix === 'CUSTOM'
                ? (this.genForm.customSuffix || '').trim() : (this.genForm.suffix || 'T');
        },
        generateMsku() {
            if (!this.parent || !this.pm || !this.shop) return this.$message.warning('缺少本地父体/品名/店铺');
            if (!this.configForm.product_type) return this.$message.warning('请先在父体配置中配置产品类型');
            if (this.genForm.suffix === 'CUSTOM' && !/^[A-Za-z0-9]{1,6}$/.test(this.effectiveGenSuffix())) {
                return this.$message.warning('MSKU 后缀需为 1-6 位英文字母或数字');
            }
            this.genError = '';
            this.genLoading = true;
            this.$axios.post('/vue/vue_product/msku-generate', {
                parent: this.parent, pm: this.pm, shop: this.shop,
                num: this.genForm.num, mode: this.genMode, suffix: this.effectiveGenSuffix(),
                // 品名级配置覆盖（以表单当前值为准，未保存时作为临时覆盖不写库）
                parent_config: {
                    series: this.configForm.series || '',
                    style: this.configForm.style || '',
                    use_msku_template: this.configForm.use_msku_template,
                    msku_separator: this.configForm.msku_separator || '-',
                    msku_template: this.configForm.msku_template || '',
                    supplier: this.configForm.supplier || '',
                    country: this.configForm.country || '',
                    // 新模板表可直接把 color 标记为 MSKU 基础名字段；未保存的表单修改也要能预览/生成
                    msku_color: this.findConfigFieldValue('msku_color')
                        || this.findMskuPartFieldValue('color') || '',
                },
            }).then(res => {
                this.genLoading = false;
                if (res.code === 200) {
                    this.genResult = res.data;
                } else {
                    this.genError = (res && res.message) || '生成失败';
                }
            }).catch(e => {
                this.genLoading = false;
                const msg = (e && e.response && e.response.data && e.response.data.message)
                    || (e && e.message) || e;
                this.genError = '生成失败: ' + msg;
            });
        },
        copyMskus() {
            const text = (this.genResult.mskus || []).join('\n');
            if (!text) return;
            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(text).then(() => {
                    this.$message.success('已复制 ' + this.genResult.mskus.length + ' 个 MSKU');
                }).catch(() => { });
            } else {
                const ta = document.createElement('textarea');
                ta.value = text;
                document.body.appendChild(ta);
                ta.select();
                document.execCommand('copy');
                document.body.removeChild(ta);
                this.$message.success('已复制 ' + this.genResult.mskus.length + ' 个 MSKU');
            }
        },
        downloadMsku() {
            if (!this.genResult) return;
            const mskus = this.genResult.mskus || [];
            if (!mskus.length) return;
            this.$axios.post('/vue/vue_product/msku-generate/download', {
                parent: this.parent, pm: this.pm, shop: this.shop,
                mskus: mskus, color: this.genResult.color || {},
            }, { responseType: 'blob' }).then(res => {
                const blob = new Blob([res], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = 'msku_' + this.parent + '.xlsx';
                link.style.display = 'none';
                document.body.appendChild(link);
                link.click();
                setTimeout(() => {
                    document.body.removeChild(link);
                    URL.revokeObjectURL(url);
                }, 300);
            }).catch(() => this.$message.error('导出失败'));
        },
        mskuMaxLen(supplier) {
            const supLen = (supplier || '').length;
            return Math.max(1, 25 - supLen);
        },
        isBoolField(f) { return f.value_type === 'boolean' || f.type === 'boolean'; },
        hasEnum(f) { return Array.isArray(f.enum) && f.enum.length > 0; },
        isNumberField(f) {
            return ['integer', 'number'].includes(f.value_type) || ['integer', 'number'].includes(f.type);
        },
        defaultFieldValue(f) {
            if (f.field_name === 'item_package_dimensions') {
                const d = f.default_value;
                if (d && typeof d === 'object') {
                    return {
                        length: d.length || 1, width: d.width || 1,
                        height: d.height || 1, unit: d.unit || 'inches',
                    };
                }
                return { length: 1, width: 1, height: 1, unit: 'inches' };
            }
            if (f.field_name === 'item_package_weight') {
                return this.normalizeWeightValue(f.default_value);
            }
            if (f.field_name === 'list_price') {
                const n = Number(f.default_value);
                return isNaN(n) ? 1 : n;
            }
            if (this.isBoolField(f)) {
                const dv = f.default_value;
                if (typeof dv === 'string') return dv.toLowerCase() === 'yes' || dv.toLowerCase() === 'true';
                return !!dv;
            }
            if (f.type === 'array') {
                // array 类型字段：default_value 可能是配置的纯值（如 brand="CoBak"）或空数组模板默认
                const dv = f.default_value;
                if (Array.isArray(dv)) return dv.length ? dv : '';
                return dv || '';
            }
            if (this.isNumberField(f)) {
                const n = Number(f.default_value);
                return isNaN(n) ? 0 : n;
            }
            return f.default_value || '';
        },
        // 重量值归一化为 {value, unit}（兼容历史存量为纯数字的情况）
        normalizeWeightValue(v) {
            if (v && typeof v === 'object') {
                return { value: v.value != null ? v.value : 1, unit: v.unit || 'pounds' };
            }
            const n = Number(v);
            return { value: isNaN(n) ? 1 : n, unit: 'pounds' };
        },
        // 三个场景字段值中的重量统一归一化（旧配置存量为纯数字时兼容）
        normalizeWeightFields() {
            ['create_field_values', 'move_in_field_values', 'move_out_field_values'].forEach(k => {
                const fv = this.configForm[k] || {};
                if (fv.item_package_weight !== undefined && fv.item_package_weight !== null) {
                    this.$set(fv, 'item_package_weight', this.normalizeWeightValue(fv.item_package_weight));
                }
            });
        },
        dimsUnitOptions(f) {
            if (f.unit_options && f.unit_options.length) return f.unit_options;
            return [
                { value: 'inches', label: 'inches' },
                { value: 'cm', label: 'cm' },
                { value: 'mm', label: 'mm' },
            ];
        },
        weightUnitOptions(f) {
            if (f.unit_options && f.unit_options.length) return f.unit_options;
            return [
                { value: 'pounds', label: 'Pounds' },
                { value: 'ounces', label: 'Ounces' },
                { value: 'kilograms', label: 'Kilograms' },
                { value: 'grams', label: 'Grams' },
            ];
        },
        saveConfig() {
            if (!this.configForm['本地品名']) return this.$message.warning('缺少本地品名');
            if (this.showWarehouse && !this.warehouse) return this.$message.warning('请选择采购仓库');
            // 产品类型可留空：品名未配置时后端会继承父体配置
            this.configSaving = true;
            this.$axios.post('/vue/vue_product/operation-config', this.configForm).then(res => {
                this.configSaving = false;
                if (res.code === 200) {
                    this.$message.success('品名配置已保存');
                    // 仅通知列表刷新，不关闭弹窗，便于继续操作
                    this.$emit('saved');
                } else {
                    this.$message.error((res && res.message) || '保存失败');
                }
            }).catch(e => {
                this.configSaving = false;
                this.$message.error('保存失败: ' + e);
            });
        },
        // 快速应用父体配置到品名（如从其它父体移入的品名）：用当前父体的父体级字段覆盖品名
        // 表单对应值，品名可修改后保存；不影响父体原有配置。
        applyParentConfig() {
            if (!this.parent) return this.$message.warning('缺少本地父体');
            this.$axios.get('/vue/vue_product/parent-config', {
                params: { parent: this.parent }
            }).then(res => {
                if (res.code !== 200 || !res.data) return this.$message.error('父体配置不存在');
                const d = res.data;
                // 父体级字段值覆盖到品名表单（父体公用字段）
                const pv = d.parent_field_values || {};
                const key = 'create_field_values';
                Object.keys(pv).forEach(fn => {
                    const v = pv[fn];
                    if (v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && v.length === 0)) {
                        this.$set(this.configForm[key], fn, v);
                    }
                });
                // 标量：品名留空的继承父体，已填的保留（避免误覆盖品名自己的款式等差异）
                [['series', 'series'], ['style', 'style'], ['msku_template', 'msku_template'],
                    ['supplier', 'supplier'], ['country', 'country'],
                    ['create_main_title', 'create_main_title'], ['create_sub_title', 'create_sub_title'],
                    ['move_out_title', 'move_out_title'],
                    ['move_out_title_differentiation', 'move_out_title_differentiation']].forEach(([src, dst]) => {
                    if (!this.configForm[dst] && d[src]) this.configForm[dst] = d[src];
                });
                if (!this.configForm.product_type && d.product_type) {
                    this.configForm.product_type = d.product_type;
                    this.loadAllFieldsAndThemes();
                }
                this.normalizeWeightFields();
                this.applySuggestedColor();
                this.$message.success('已应用父体配置（可修改后保存）');
            }).catch(() => this.$message.error('应用父体配置失败'));
        },
    },
};
</script>

<style scoped>
.config-meta { display: flex; flex-wrap: wrap; gap: 8px 28px; font-size: 13px; color: #606266;
    background: #f5f7fa; border-radius: 4px; padding: 10px 14px; margin-bottom: 12px; }
.meta-item { display: inline-flex; align-items: center; }
.meta-label { color: #909399; margin-right: 6px; }
.pm-template-apply { padding: 10px 14px; margin-bottom: 12px; border: 1px solid #d9ecff;
    background: #f4f9ff; border-radius: 4px; }
.pm-template-title { color: #409eff; font-size: 13px; font-weight: 600; margin-bottom: 8px; }
.pm-template-controls { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.config-layout { display: flex; gap: 20px; align-items: flex-start; }
.config-left { width: 440px; flex-shrink: 0; border-right: 1px solid #ebeef5; padding-right: 20px;
    max-height: 560px; overflow-y: auto; }
.config-right { flex: 1; min-width: 0; max-height: 560px; overflow-y: auto; padding-left: 6px; }
.config-empty { color: #909399; font-size: 13px; padding: 12px 0; }
.msku-hint { font-size: 12px; color: #909399; line-height: 1.6; }
.msku-preview { font-size: 12px; color: #409eff; line-height: 1.6; background: #ecf5ff;
    border: 1px solid #d9ecff; border-radius: 4px; padding: 6px 10px; word-break: break-all; }
.msku-preview-warn { color: #CC0000; background: #fef0f0; border-color: #fbc4c4; font-weight: bold; }
.msku-preview-warn-text { font-size: 12px; color: #CC0000; line-height: 1.6; margin-top: 4px; }
.dims-row { display: flex; align-items: center; gap: 6px; }
.dims-x { color: #909399; }
</style>

<!-- 全局样式：弹窗 append-to-body 渲染在 body 下，scoped 深度选择器无法命中 -->
<style>
/* 仅最外层样式适配：弹窗整体上移 100px（15vh 默认垂直位置基础上再向上偏移），内部结构不变 */
.pm-config-dialog { margin-top: max(0px, calc(15vh - 100px)) !important; }
</style>
